<template>
  <div id="essay-detail">
    <b-container fluid class="background">
      <b-row class="pt-4 pb-2 responsive-padding">
        <b-col class="px-0">
          <a class="strip top-back-link" @click="$router.push({ name: 'FeedbackHistory' })">&larr; Back to History</a>
        </b-col>
      </b-row>

      <!-- Loading state -->
      <div v-if="loading" class="text-center py-5">
        <b-spinner label="Loading..."></b-spinner>
        <p class="mt-3 text-muted">Loading your essay...</p>
      </div>

      <!-- Not found -->
      <div v-else-if="!submission" class="text-center py-5">
        <p class="text-muted">This essay could not be found.</p>
        <b-button variant="primary" @click="$router.push({ name: 'FeedbackHistory' })">
          Back to History
        </b-button>
      </div>

      <template v-else>
        <!-- Draft state -->
        <template v-if="submission.status === 'draft'">
          <section class="page-hero responsive-padding">
            <h1 class="text-center prompt-title page-title">Draft Essay</h1>
            <p class="page-subtitle">
              Last updated {{ formatDate(submission.updatedAt || submission.createdAt) }}
              <span v-if="submission.promptId"> &bull; Prompt {{ submission.promptId }}</span>
            </p>
          </section>

          <b-row class="px-1 px-sm-4 responsive-padding mx-0 mx-sm-4">
            <b-col cols="12" class="px-0">
              <b-card class="draft-card">
                <h2 class="draft-heading mb-3">
                  {{ submission.promptText || "No prompt recorded" }}
                </h2>
                <p v-if="submission.moduleVersion" class="text-muted mb-3">
                  Module: {{ submission.moduleVersion }}
                </p>
                <div class="draft-body mb-3">{{ submission.essayText || "Draft is empty." }}</div>
                <b-button variant="primary" @click="continueWriting">
                  Continue Writing
                </b-button>
              </b-card>
            </b-col>
          </b-row>
        </template>

        <!-- Pending state -->
        <div v-else-if="submission.status === 'pending'" class="submission-loading py-5 text-center">
          <div class="submission-card mx-auto">
            <b-spinner class="submission-spinner mb-3" label="Generating feedback"></b-spinner>
            <h2 class="submission-title">Your essay has been submitted.</h2>
            <p class="submission-subtitle">Generating your feedback...</p>
            <p class="submission-meta">This usually takes about 20–40 seconds.</p>
            <b-progress class="submission-progress mb-3" :max="100" height="8px">
              <b-progress-bar :value="100" animated></b-progress-bar>
            </b-progress>
            <p class="submission-safe mb-0">Your work is safe — this page will update automatically.</p>
          </div>
        </div>

        <!-- Error state -->
        <div v-else-if="submission.status === 'error'" class="py-5 text-center">
          <b-alert show variant="danger" class="mx-auto" style="max-width: 600px;">
            <strong>Feedback generation failed.</strong>
            <span v-if="submission.errorMessage"> {{ submission.errorMessage }}</span>
          </b-alert>
          <p class="text-muted mt-3">Your essay text was saved. Return to the essay to resubmit.</p>
          <b-button variant="primary" @click="$router.push({ name: 'SelectModule' })">
            Try Again
          </b-button>
        </div>

        <!-- Complete state with feedback -->
        <template v-else-if="submission.status === 'complete'">
          <section class="page-hero responsive-padding">
            <h1 class="text-center prompt-title page-title">Feedback on Your Essay</h1>
            <p class="page-subtitle">
              Submitted {{ formatDate(submission.createdAt) }}
              <span v-if="submission.moduleVersion"> &bull; {{ submission.moduleVersion }}</span>
            </p>
          </section>

          <b-row class="px-1 px-sm-4 white-container responsive-padding mx-0 mx-sm-4">
            <essayhead :prompt="promptObj" :score="score"></essayhead>
            <sectionfeedback
              class="mt-4"
              v-for="section in stream"
              :key="section.title"
              :title="section.title"
              :points="section.points"
              :content="section.content"
              :title_2="section.title_2"
              :points_2="section.points_2"
              :content_2="section.content_2"
            ></sectionfeedback>
          </b-row>
        </template>

        <!-- Unknown state safeguard -->
        <div v-else class="py-5 text-center">
          <b-alert show variant="warning" class="mx-auto" style="max-width: 600px;">
            This essay has an unsupported status: {{ submission.status || "unknown" }}
          </b-alert>
          <b-button variant="primary" @click="$router.push({ name: 'FeedbackHistory' })">
            Back to My Essays
          </b-button>
        </div>
      </template>
    </b-container>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useRoute, useRouter } from "vue-router";
import { subscribeToEssaySubmission } from "@/services/essaySubmissions";
import essayhead from "@/components/writing_feature/LEQEssay/EssayHead.vue";
import sectionfeedback from "@/components/writing_feature/SectionFeedback.vue";

export default {
  name: "EssayDetailView",
  components: { essayhead, sectionfeedback },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const submissionId = route.params.submissionId;

    const submission = ref(null);
    const loading = ref(true);
    const score = ref(null);
    const stream = ref([]);

    let unsubscribe = null;

    // ---- Section rendering helpers (mirrors Feedback.vue logic) ----

    const sectionDisplayTitleMap = {
      contextualization: "Contextualization",
      thesis: "Thesis Statement",
      conclusion: "Conclusion"
    };

    const formatSectionTitle = key => {
      if (!key || typeof key !== "string") return "Section";
      return key
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/([a-zA-Z])(\d+)/g, "$1 $2")
        .replace(/[_-]+/g, " ")
        .trim()
        .replace(/\b\w/g, c => c.toUpperCase());
    };

    const getPairNumber = (key, prefix) => {
      if (typeof key !== "string" || !key.startsWith(prefix)) return null;
      const suffix = key.slice(prefix.length);
      const value = Number(suffix);
      return Number.isInteger(value) && value > 0 ? value : null;
    };

    const getSectionRank = key => {
      if (key === "contextualization") return 0;
      if (key === "thesis") return 1;
      const ep = getPairNumber(key, "evidence");
      if (ep !== null) return 100 + ep * 2;
      const ap = getPairNumber(key, "analysis");
      if (ap !== null) return 101 + ap * 2;
      if (key === "conclusion") return 100000;
      return 1000;
    };

    const orderSectionKeys = sections => {
      return Object.keys(sections || {}).sort((a, b) => getSectionRank(a) - getSectionRank(b) || a.localeCompare(b));
    };

    const toSectionContent = section => ({
      userInput: section?.whatWasWritten || "",
      good: section?.whatWasGood || "",
      toImprove: section?.whatToImprove || ""
    });

    const toPoints = section => {
      const s = Number(section?.score ?? 0);
      const max = Number(section?.maxScore ?? 0);
      return max === 0 ? "Optional" : `${s}/${max}`;
    };

    const buildStream = backendFeedback => {
      const sections = backendFeedback?.sections;
      if (!sections || typeof sections !== "object") {
        stream.value = [];
        return;
      }

      const orderedKeys = orderSectionKeys(sections);
      const availableKeys = new Set(orderedKeys);
      const nextStream = [];

      const pushSingle = key => {
        if (!availableKeys.has(key)) return;
        const sec = sections[key] || {};
        nextStream.push({
          title: sectionDisplayTitleMap[key] || formatSectionTitle(key),
          points: toPoints(sec),
          content: toSectionContent(sec)
        });
        availableKeys.delete(key);
      };

      pushSingle("contextualization");
      pushSingle("thesis");

      const pairIndexes = new Set();
      orderedKeys.forEach(key => {
        const ep = getPairNumber(key, "evidence");
        if (ep !== null) pairIndexes.add(ep);
        const ap = getPairNumber(key, "analysis");
        if (ap !== null) pairIndexes.add(ap);
      });

      Array.from(pairIndexes).sort((a, b) => a - b).forEach(i => {
        const ek = `evidence${i}`, ak = `analysis${i}`;
        if (!availableKeys.has(ek) && !availableKeys.has(ak)) return;
        const es = sections[ek] || {}, as_ = sections[ak] || {};
        nextStream.push({
          title: `Evidence #${i}`,
          points: toPoints(es),
          content: toSectionContent(es),
          title_2: "Analysis",
          points_2: toPoints(as_),
          content_2: toSectionContent(as_)
        });
        availableKeys.delete(ek);
        availableKeys.delete(ak);
      });

      if (availableKeys.has("conclusion")) {
        const c = sections.conclusion || {};
        nextStream.push({ title: "Conclusion", points: toPoints(c), content: toSectionContent(c) });
        availableKeys.delete("conclusion");
      }

      Array.from(availableKeys).forEach(key => {
        const sec = sections[key] || {};
        nextStream.push({ title: sectionDisplayTitleMap[key] || formatSectionTitle(key), points: toPoints(sec), content: toSectionContent(sec) });
      });

      stream.value = nextStream;
    };

    const handleSubmissionUpdate = data => {
      submission.value = data;
      loading.value = false;

      if (data && data.status === "complete" && data.feedback) {
        const bf = data.feedback;
        score.value = bf.displayScore ?? bf.overallScore ?? bf.score ?? null;
        buildStream(bf);
      }
    };

    onMounted(() => {
      unsubscribe = subscribeToEssaySubmission(
        submissionId,
        handleSubmissionUpdate,
        err => {
          console.error("Failed to load submission:", err);
          loading.value = false;
        }
      );
    });

    onBeforeUnmount(() => {
      if (unsubscribe) unsubscribe();
    });

    const promptObj = computed(() => {
      if (!submission.value) return null;
      return { prompt: submission.value.promptText || "" };
    });

    const formatDate = ts => {
      if (!ts) return "";
      const d = ts.toDate ? ts.toDate() : new Date(ts);
      return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    };

    const continueWriting = () => {
      const promptId = submission.value?.promptId;
      if (!promptId) {
        router.push({ name: "001" });
        return;
      }

      router.push({
        name: "003",
        params: {
          id: String(promptId),
          module: submission.value?.moduleVersion || "Expert"
        }
      });
    };

    return { submission, loading, score, stream, promptObj, formatDate, continueWriting };
  }
};
</script>

<style scoped>
#essay-detail {
  min-height: 100vh;
  background: #f9f9fb;
}

.top-back-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid #8c30f5;
  background: transparent;
  font-size: 1rem;
  font-weight: 700;
  color: #8c30f5;
  transition: background 0.15s ease, color 0.15s ease;
}

.top-back-link:hover {
  background: #8c30f5;
  color: #fff;
}

.page-hero {
  text-align: center;
  padding: 2rem 1rem 1rem;
}

.page-title {
  font-size: 1.8rem;
  font-weight: 700;
  color: #1a1a2e;
}

.page-subtitle {
  color: #666;
  font-size: 0.95rem;
}

.submission-card {
  max-width: 480px;
  background: #fff;
  border-radius: 16px;
  padding: 2.5rem 2rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
}

.submission-title {
  font-size: 1.25rem;
  font-weight: 700;
}

.submission-subtitle,
.submission-meta,
.submission-safe {
  font-size: 0.9rem;
  color: #666;
}

.submission-progress {
  width: 100%;
}

.draft-card {
  border-radius: 12px;
  border: 1px solid #e0e0e8;
}

.draft-heading {
  font-size: 1.25rem;
  color: #1a1a2e;
}

.draft-body {
  white-space: pre-wrap;
  line-height: 1.6;
  color: #222;
}

.responsive-padding {
  padding-left: 1.5rem;
  padding-right: 1.5rem;
}

@media (min-width: 576px) {
  .responsive-padding {
    padding-left: 2.5rem;
    padding-right: 2.5rem;
  }
}
</style>
